// Absolute URL helpers for crawler-facing metadata.
//
// Open Graph and Twitter card scrapers do not resolve relative URLs against the
// page they are scraping: Facebook, LinkedIn, Slack and Twitter all require a
// fully qualified http(s) URL in og:image / twitter:image, and silently drop
// the image otherwise. Anything emitted into <head> for a scraper therefore has
// to go through toAbsoluteUrl first.

/**
 * The origin used when nothing better is available, i.e. during the build-time
 * prerender where there is no `window`. Marketing pages are only ever served
 * from the apex domain, so this is the correct canonical origin for them.
 */
export const CANONICAL_ORIGIN = 'https://statharbour.com';

/** The sitewide fallback share card, generated by scripts/generate-og-default.mjs. */
export const DEFAULT_OG_IMAGE_PATH = '/og-image.png';

/** Dimensions of every card StatHarbour generates (static and dynamic alike). */
export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;

/**
 * The origin of the page currently being rendered. Falls back to the canonical
 * origin on the server (prerender) where `window` does not exist.
 *
 * On the demo and esports subdomains this correctly returns that subdomain, so
 * a shared demo link points at itself rather than at the apex domain.
 */
export const getSiteOrigin = (): string =>
  typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : CANONICAL_ORIGIN;

/**
 * Resolves a possibly-relative URL to an absolute one.
 *
 * Returns null for empty input, and leaves already-absolute URLs (including
 * `data:` URIs, which some organizer logos are) untouched.
 */
export const toAbsoluteUrl = (
  value: string | null | undefined,
  origin: string = getSiteOrigin(),
): string | null => {
  if (!value) return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  // Already absolute, or a data/blob URI that must not be prefixed.
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;

  // Protocol-relative (//cdn.example.com/x.png): adopt the page's scheme.
  if (trimmed.startsWith('//')) {
    const scheme = origin.startsWith('http://') ? 'http:' : 'https:';
    return `${scheme}${trimmed}`;
  }

  const base = origin.replace(/\/$/, '');
  return trimmed.startsWith('/') ? `${base}${trimmed}` : `${base}/${trimmed}`;
};

/**
 * Builds the URL of the dynamically generated Open Graph card for a league
 * (see api/og.tsx). Used by both the SPA and the server-rendered public page,
 * so a shared link unfurls with the league's own standings.
 */
export const buildLeagueOgImageUrl = (
  leagueId: string,
  seasonId?: string | null,
  origin: string = getSiteOrigin(),
): string => {
  const base = origin.replace(/\/$/, '');
  const params = new URLSearchParams({ league: leagueId });
  if (seasonId) params.set('season', seasonId);
  return `${base}/api/og?${params.toString()}`;
};
